import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { API_REFRESH_TOKEN, API_URL } from './apiConfig';

const isDevelopment = process.env.NODE_ENV === 'development';

// Helpers de storage para manter consistência (local + session)
const TOKEN_KEY = 'token';
const REFRESH_KEY = 'refreshToken';

const getStoredToken = () =>
  localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY);

const setStoredToken = (token: string) => {
  localStorage.setItem(TOKEN_KEY, token);
  sessionStorage.setItem(TOKEN_KEY, token);
  (http.defaults.headers as any).Authorization = `Bearer ${token}`;
  (http.defaults.headers.common as any).Authorization = `Bearer ${token}`;
};

const getStoredRefresh = () =>
  localStorage.getItem(REFRESH_KEY) || sessionStorage.getItem(REFRESH_KEY);

const clearStoredTokens = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  delete (http.defaults.headers as any).Authorization;
  delete (http.defaults.headers.common as any).Authorization;
};

// Instancia base com URL da API
const http = axios.create({
  baseURL: API_URL,
});

// Log condicional (apenas em dev)
const log = (...args: unknown[]) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.log(...args);
  }
};
const warn = (...args: unknown[]) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.warn(...args);
  }
};
const errorLog = (...args: unknown[]) => {
  if (isDevelopment) {
    // eslint-disable-next-line no-console
    console.error(...args);
  }
};

const formatJson = (data: unknown) => {
  if (typeof data === 'string') {
    return data;
  }

  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
};

// Bootstrap do header se já houver token salvo
const bootstrapToken = getStoredToken();
if (bootstrapToken) {
  (http.defaults.headers as any).Authorization = `Bearer ${bootstrapToken}`;
}

// Interceptador para anexar token JWT automaticamente
http.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredToken();
    config.headers = config.headers ?? {};

    if (token) {
      (config.headers as any).Authorization = `Bearer ${token}`;
    } else {
      delete (config.headers as any).Authorization;
      delete (http.defaults.headers as any).Authorization;
      delete (http.defaults.headers.common as any).Authorization;
      warn('Nenhum token encontrado para enviar nas requisicoes.');
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Interceptador de resposta com renovacao automatica de token
http.interceptors.response.use(
  (response) => {
    log('HTTP RESPONSE', response.status, response.config.url);
    if (isDevelopment) {
      // eslint-disable-next-line no-console
      console.log('[HTTP RESPONSE DATA]', formatJson(response.data));
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Log detalhado de erros
    if (error.response) {
      console.error('❌ [HTTP ERROR]', {
        url: error.config?.url,
        method: error.config?.method?.toUpperCase(),
        status: error.response.status,
        data: error.response.data,
      });
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error('[HTTP ERROR DATA]', formatJson(error.response.data));
      }
      
      // Normalizar formato de erro do backend
      const responseData = error.response.data as any;
      if (error.response.status === 400 && responseData) {
        console.log('🔍 [HTTP] Analisando erro 400:', responseData);
        console.log('🔍 [HTTP] Tem details?', !!responseData.details);
        console.log('🔍 [HTTP] É objeto?', typeof responseData === 'object');
        
        // Se não está no formato padronizado, tentar converter
        if (!responseData.details && typeof responseData === 'object') {
          // Pode ser Django REST Framework retornando erros diretamente
          // Ex: {"field": ["error message"]}
          const hasArrayValues = Object.values(responseData).some(v => Array.isArray(v));
          console.log('🔍 [HTTP] Tem arrays?', hasArrayValues);
          
          if (hasArrayValues) {
            console.warn('⚠️ Convertendo formato de erro DRF para padrão:', responseData);
            error.response.data = {
              error: true,
              status_code: 400,
              message: 'Ocorreu um erro no processamento da requisição',
              details: responseData,
            };
            console.log('✅ [HTTP] Erro convertido:', error.response.data);
          }
        } else if (responseData.details) {
          console.log('✅ [HTTP] Erro já está no formato padrão com details');
        }
      }
    }

    if (error.response?.status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      const refreshToken = getStoredRefresh();

      if (refreshToken) {
        try {
          const resp = await axios.post(
            API_REFRESH_TOKEN,
            { refresh: refreshToken },
            { baseURL: API_URL, headers: { 'Content-Type': 'application/json' } },
          );

          const newToken = (resp.data as any).access;
          if (newToken) {
            setStoredToken(newToken);
            originalRequest.headers = originalRequest.headers ?? {};
            (originalRequest.headers as any).Authorization = `Bearer ${newToken}`;
            return http(originalRequest);
          }
        } catch (refreshError) {
          errorLog('Falha ao renovar token', refreshError);
          clearStoredTokens();
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  },
);

export default http;
